
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<h1>{`Functions`}</h1>
<p>{`Always use function expressions instead of function declarations.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = function () {
};

// Good
function foo() {
}
`}</code></pre>
<p>{`Always use arrow functions when declaring immediately invoked functions.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
(function () {
  // ... more code ...
})();

// Good
(() => {
  // ... more code ...
})();
`}</code></pre>
<p>{`Never use `}<inlineCode parentName="p">{`arguments`}</inlineCode>{`.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? Since rest arguments are available, that's an obsolete practice. Plus, it's not supported in arrow functions.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo() {
  const args = Array.prototype.slice.call(arguments);
  return args[0] + args[1];
}

// Good
function foo(...args) {
  return args[0] + args[1];
}
`}</code></pre>
<p>{`Always use default parameters instead of mutating an argument when a default value is needed.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(arg) {
  arg = arg || {};
  // ... more code ...
}

// Good
function foo(arg = {}) {
  // ... more code ...
}
`}</code></pre>
<p>{`When using keyword arguments (an object), always put them in the last position.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(kwargs, arg) {
  return kwargs.bar + arg;
}

// Good
function foo(arg, kwargs) {
  return kwargs.bar + arg;
}
`}</code></pre>
<p>{`When using optional keyword arguments, always assign a default value of an empty object to them.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(arg, kwargs) {
  // ... more code ...
}

foo('Hello');  // Can fail if the function tries to access kwargs in the body, leading to control statements

// Good
function foo(args, kwargs = {}) {
  // ... more code ...
}

foo('Hello');  // Would lead to cleaner code inside the function body
`}</code></pre>
<p>{`Never use `}<inlineCode parentName="p">{`eval()`}</inlineCode>{` for creating a dynamic function. Intead use `}<inlineCode parentName="p">{`Function`}</inlineCode>{`.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = eval('function (a, b) { return a + b }');

// Good
const foo = Function('a', 'b', 'return a + b');
`}</code></pre>
<h5><strong parentName="h5">{`NOTE`}</strong>{`: Think twice if you ever need to do this.`}</h5>
<p>{`Avoid re-assigning arguments. Only good use case is when some sanitization of the arguments is needed.`}</p>
<blockquote>
<p parentName="blockquote">{`If we mutate arguments, it's complicated to know further in the body of the function if the argument still holds the original value or not. When sanitizing, we're working with the intended value of that argument, so no confusion is present.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad 
function foo(arg) {
  arg = 2;
  return arg;
}

// Good
function foo(arg) {
  arg = sanitize(arg);
  return arg;
}
`}</code></pre>
<p>{`Never mutate arguments. Instead, clone them.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(arg) {
  arg[0] = 'Bar';
}

// Good
function foo(arg) {
  const newArg = arg.slice();
  newArg[0] = 'Bar';
}
`}</code></pre>
<p>{`Use the spread operator `}<inlineCode parentName="p">{`...`}</inlineCode>{` to call functions with different number of arguments intead of `}<inlineCode parentName="p">{`call()`}</inlineCode>{` or `}<inlineCode parentName="p">{`apply()`}</inlineCode></p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(array) {
  bar.apply(null, array);
}

// Good
function foo(array) {
  bar(...array);
}
`}</code></pre>
<p>{`When declaring standalone functions, always use `}<inlineCode parentName="p">{`function`}</inlineCode>{` keyword.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = () => {
  return 'bar';
};

// Good
function foo() {
  return 'bar';
}
`}</code></pre>
<p>{`When using function assignment, always use arrow functions.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = function () {
};

// Good
const foo = () => {
};
`}</code></pre>
<p>{`When declaring a function in the body of another function, always use function assigment.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? Those functions can be thought as just variables like `}<inlineCode parentName="p">{`const a = 1`}</inlineCode>{`, the only difference is that they contain a function value instead of a complex or scalar vlaue, so consistency dictates, we use the same assignment expression.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(arg) {
  const coef = 0.1;

  function bar() {
    // ... more code ...
  }

  return bar(arg * coef);
}

// Good
function foo(arg) {
  const coef = 0.1;
  const bar = () => {
    // ... more code ...
  };

  return bar(arg * coef);
}
`}</code></pre>
<p>{`Always declare pure functions as standalone functions. Always declare non-pure functions as close to the external parameters as possible.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(number1, number2) {
  const add = (a, b) => {
    return a + b;
  };
  return add(number1, number2);
}

function bar(number1) {
  const number2 = 2;

  // ... more code ...

  const add = (a) => {
    return a + number2;
  };

  return add(number1);
}

// Good
function add(a, b) {
  return a + b;
}

function foo(number1, number2) {
  return add(number1, number2);
}

function bar(number1) {
  const number2 = 2;

  const add = (a) => {
    return a + number2;
  };

  return add(number1);
}
`}</code></pre>
<p>{`Always declare scalar and complex values before, and functions after.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo() {
  const a = 1;
  const bar = () => a + 2;  
  const b = 2;
  return bar() + b;
}

// Good
function foo() {
  const a = 1;
  const b = 2;
  const bar = () => a + 2;
  return bar() + b;
}
`}</code></pre>
<p>{`Always use arrow functions for anonymous functions.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
array.map(function (item) {
  return item * 2;
});

// Good
array.map((item) => item * 2);
`}</code></pre>
<p>{`Always use parentheses when declaring arrow functions.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? Consistency again, when we have only one argument, we can choose to use parentheses or not but when having multiple arguements, we're forced to use parentheses. We remove the choice so we can focus more on the code. Also, it will lead to simpler changes if later we need to add more arguments to a single argument function.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = arg => arg + 2;

// Good
const foo = (arg) => arg + 2;
`}</code></pre>
<p>{`If the list of arguments of a function is too long, favor using keyword arguments instead.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(name, lastName, age, country, region, username, password) {
  // ... more code ...
}

// Good
function foo({ name, lastName, age, country, region, username, password }) {
  // ... more code ...
}
`}</code></pre>
<p>{`Always favor writing pure functions.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo() {
  const a = 1;
  const bar = () => a + 2;
  return bar();
}

// Good
function bar(a) {
  return a + 2;
}

function foo() {
  const a = 1;
  return bar(a);
}
`}</code></pre>
<p>{`When using arrow functions, never split the implicit return between lines. Use `}<inlineCode parentName="p">{`{}`}</inlineCode>{` instead.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = () =>
  'A very long return value that would not fit in the same line';

const foo = () => (
  'A very long return value that would not fit in the same line';
);

// Good
const foo = () => {
  return 'A very long return value that would not fit in the same line';
};
`}</code></pre>
<p>{`Declare functions before using them.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? Apart from difficulting reading the code (you won't expect to understand a word before you've seen its definition) it relies on hoisting for it to work, one of the worst things JavaScript does and one that people is still actively investigating how to remove without breaking existing code.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo() {
  return bar();
}

function bar() {
  // ... more code ...
}

// Good
function bar() {
  // ... more code ...
}

function foo() {
  return bar();
}
`}</code></pre>
<p>{`If an object it's the only thing an arrow function returns, use `}<inlineCode parentName="p">{`()`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`return`}</inlineCode>{`.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const foo = () => {
  return { 
    name: 'User',
  };
};

// Good
const foo = () => ({
  name: 'User',
});
`}</code></pre>
<p>{`When writing arrow functions, always use single line functions if possible.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
array.map((item) => {
  return item * 2;
});

// Good
array.map((item) => item * 2);
`}</code></pre>
<p>{`Never use implicit return with side effects.`}</p>
<pre><code parentName="pre" {...{}}>{`let bar = true;

// Bad
const foo = () => bar = false;

// Good
const foo = () => {
  bar = false;
};
`}</code></pre>
<p>{`When returning multiple data, always use object destructuring instead of array destructuring.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? It makes returned result not order dependant when using the function.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo() {
  const a = 1;
  const b = 2;
  return [a, b];
}

// Good
function foo() {
  const a = 1;
  const b = 2;
  return { a, b };
}
`}</code></pre>
<p>{`Always favor returning early.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(a, b) {
  if (a !== b) {
    // ... more code ...
  }
  return null;
}

// Good
function foo(a, b) {
  if (a === b) {
    return null;
  }

  // ... more code ...
}
`}</code></pre>
<p>{`Add one level of indentation when splitting the list of arguments over multiple lines.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
function foo(
item1,
item2,
item3,
item4,
) {
  // ... more code ...
}

function foo(item1, item2,
  item3, item4) {
  // ... more code ...
}

// Good
function foo(
  item1,
  item2,
  item3,
  item4,
) {
  // ... more code ...
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;